import { DateTime } from 'luxon'

import type { OneTimeAcknowledgement } from '@/queries/constants'
import UserQ from '@/queries/UserQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import RPC, { RPCRefresh } from '@/client/rpc'

import useAPIWrapper from './useAPIWrapper'

/**
 * NOTE: This function does not return a stable return value. It's useless using the value
 * in a hook dependency.
 */
export default function useOneTimeAcknowledgements(
  keys: OneTimeAcknowledgement[],
  options: { disableRefresh?: boolean } = {}
): {
  key: string
  acknowledgedAt: DateTime | null
  onAcknowledgement: () => Promise<void>
}[] {
  const user = useUser()
  const query = useQuery()

  const me = query.one(UserQ.where({ id: user.id }))
  const acknowledgedAtDates = keys
    .map(key => me`(${UserQ.internal_state}->>${key})::timestamp`)
    .map(date => (date != null ? DateTime.fromISO(date) : null))

  const disableRefresh = options.disableRefresh ?? false

  const [onAcknowledgement] = useAPIWrapper(
    async (key: OneTimeAcknowledgement) => {
      if (disableRefresh) {
        await RPC.oneTimeAcknowledgement({ key })
      } else {
        await RPCRefresh.oneTimeAcknowledgement({ key })
      }
    },
    [disableRefresh],
    `One-Time Acknowledgement of one of ${keys.join(',')} `
  )

  return keys.map((key, i) => ({
    key,
    acknowledgedAt: acknowledgedAtDates[i],
    onAcknowledgement: () => onAcknowledgement(key),
  }))
}
